import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { clubSidebar } from '@/config/sidebarConfig.ts';
import { getTeamsApi } from '@/lib/api/teamsApi';
import { getPageNumbers } from '@/lib/pagination';
import { queryKeys } from '@/lib/query/queryKeys';
import { getTeamSportingCaptain } from '@/lib/team-sporting-captain';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr } from '@shared';
import { PaymentStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import type { Team } from '@shared/types/entities';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  BadgeCheck,
  Calendar,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock,
  CreditCard,
  Eye,
  MapPin,
  Search,
  ShieldCheck,
  Users,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Badge from '../../components/ui/Badge';
import Dialog from '../../components/ui/Dialog';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

const PAGE_SIZE = 20;
const GRADIENT_COLORS = [
  'from-orange-500 to-red-600',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-green-600',
  'from-purple-500 to-violet-600',
];

const REG_STATUS_CONFIG: Record<
  TeamRegistrationStatus,
  { label: string; variant: 'success' | 'warning' | 'danger' | 'info' }
> = {
  [TeamRegistrationStatus.CONFIRMED]: { label: 'Confirmee', variant: 'success' },
  [TeamRegistrationStatus.PENDING]: { label: 'En attente', variant: 'warning' },
  [TeamRegistrationStatus.REJECTED]: { label: 'Rejetee', variant: 'danger' },
  [TeamRegistrationStatus.CANCELLED]: { label: 'Annulee', variant: 'info' },
};

const PAY_STATUS_CONFIG: Record<PaymentStatus, { label: string; variant: 'success' | 'warning' | 'danger' | 'info' }> =
  {
    [PaymentStatus.COMPLETED]: { label: 'Paye', variant: 'success' },
    [PaymentStatus.PENDING]: { label: 'En attente', variant: 'warning' },
    [PaymentStatus.FAILED]: { label: 'Echoue', variant: 'danger' },
    [PaymentStatus.REFUNDED]: { label: 'Rembourse', variant: 'info' },
    [PaymentStatus.PARTIALLY_REFUNDED]: { label: 'Remb. partiel', variant: 'info' },
  };

const VALID_REG_STATUSES = new Set<string>(Object.values(TeamRegistrationStatus));
const VALID_PAY_STATUSES = new Set<string>(Object.values(PaymentStatus));

function getInitials(firstName: string | null, lastName: string | null): string {
  const f = firstName?.[0] ?? '';
  const l = lastName?.[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

// ─── Detail Modal ───────────────────────────────────────────
function TeamDetailDialog({ team, onClose }: { team: Team | null; onClose: () => void }) {
  if (!team) return null;
  const comp = team.competition;
  const regCfg = REG_STATUS_CONFIG[team.registrationStatus];
  const payCfg = PAY_STATUS_CONFIG[team.paymentStatus];

  return (
    <Dialog open={!!team} onClose={onClose} title={team.name}>
      <div className='space-y-4'>
        {/* Status */}
        <div className='flex flex-wrap gap-2'>
          <Badge variant={regCfg.variant}>{regCfg.label}</Badge>
          <Badge variant={payCfg.variant}>{payCfg.label}</Badge>
        </div>

        {/* Competition */}
        {comp && (
          <div className='rounded-xl bg-slate-50 p-4'>
            <p className='font-semibold text-slate-900'>{comp.name}</p>
            <div className='mt-1 flex flex-wrap gap-3 text-sm text-slate-500'>
              {comp.date && (
                <span className='flex items-center gap-1'>
                  <Calendar className='h-3.5 w-3.5' />
                  {formatDateAbbreviatedFr(comp.date)}
                </span>
              )}
              {comp.venue && (
                <span className='flex items-center gap-1'>
                  <MapPin className='h-3.5 w-3.5' />
                  {comp.venue}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Players */}
        {team.players && team.players.length > 0 && (
          <div>
            <p className='mb-2 text-sm font-semibold text-slate-700'>Joueurs</p>
            <div className='space-y-2'>
              {team.players.map((p, idx) => (
                <div key={p.id} className='flex items-center gap-3'>
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br ${GRADIENT_COLORS[idx % GRADIENT_COLORS.length]} text-xs font-bold text-white`}
                  >
                    {getInitials(p.firstName, p.lastName)}
                  </div>
                  <span className='text-sm text-slate-800'>
                    <span className='font-medium'>
                      {p.firstName} {p.lastName}
                    </span>
                    {p.licenseNumber && (
                      <span className='ml-2 font-mono text-xs text-slate-400'>Licence : {p.licenseNumber}</span>
                    )}
                    {p.licenseAutoDeclaredAt ? (
                      <span className='ml-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-600'>
                        <ShieldCheck className='h-3 w-3' />
                        Déclarée sur l&apos;honneur le {formatDateAbbreviatedFr(p.licenseAutoDeclaredAt)}
                      </span>
                    ) : (
                      p.licenseNumber && (
                        <span className='ml-2 text-xs text-slate-400'>Déclaration non disponible (ancien système)</span>
                      )
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer info */}
        <div className='grid grid-cols-3 gap-4 border-t border-slate-100 pt-4'>
          <div>
            <p className='text-xs text-slate-500'>Inscription</p>
            <p className='font-semibold text-slate-900 text-sm'>{formatDateAbbreviatedFr(team.createdAt)}</p>
          </div>
          <div>
            <p className='text-xs text-slate-500'>Montant</p>
            <p className='font-semibold text-slate-900 text-sm'>
              {team.paidAmount != null ? `${(team.paidAmount / 100).toFixed(2)} €` : '—'}
            </p>
          </div>
          <div>
            <p className='text-xs text-slate-500'>Capitaine</p>
            <p className='font-semibold text-slate-900 text-sm truncate'>
              {getTeamSportingCaptain(team)
                ? `${getTeamSportingCaptain(team)?.firstName} ${getTeamSportingCaptain(team)?.lastName}`
                : '—'}
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

// ─── Page ───────────────────────────────────────────────────
export default function ClubInscriptions() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clubId = user?.clubId ?? undefined;

  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') ?? '1', 10);
  const search = searchParams.get('search') ?? '';
  const regStatusParam = searchParams.get('regStatus') ?? '';
  const payStatusParam = searchParams.get('payStatus') ?? '';
  const regStatus = VALID_REG_STATUSES.has(regStatusParam) ? (regStatusParam as TeamRegistrationStatus) : undefined;
  const payStatus = VALID_PAY_STATUSES.has(payStatusParam) ? (payStatusParam as PaymentStatus) : undefined;

  const setParam = (key: string, value: string) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key !== 'page') next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  // Debounce search
  const [searchInput, setSearchInput] = useState(search);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('search', searchInput);
          else next.delete('search');
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    }, 350);
    return () => clearTimeout(searchTimeout.current);
  }, [searchInput, setSearchParams]);

  const teamsParams = {
    clubId,
    page,
    limit: PAGE_SIZE,
    ...(search && { search }),
    ...(regStatus && { registrationStatus: regStatus }),
    ...(payStatus && { paymentStatus: payStatus }),
    sortBy: 'createdAt',
    sortOrder: 'desc' as const,
  };

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey: queryKeys.teams.list(teamsParams),
    queryFn: () => getTeamsApi(teamsParams),
    enabled: !!clubId && isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const teams = useMemo<Team[]>(() => data?.data ?? [], [data]);
  const total = data?.meta?.total ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const confirmed = useMemo(
    () => teams.filter(t => t.registrationStatus === TeamRegistrationStatus.CONFIRMED).length,
    [teams],
  );
  const pending = useMemo(
    () => teams.filter(t => t.registrationStatus === TeamRegistrationStatus.PENDING).length,
    [teams],
  );

  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={clubSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      <div className='p-8'>
        <PageHeader title='Mes Inscriptions' subtitle='Suivi des inscriptions de vos equipes' />

        {/* Stats */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <StatCard
            icon={ClipboardCheck}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Total inscriptions'
            value={isLoading ? '—' : String(total)}
            subtitle='Toutes equipes'
          />
          <StatCard
            icon={CheckCircle}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Confirmees'
            value={isLoading ? '—' : String(confirmed)}
            subtitle='Paiements valides'
          />
          <StatCard
            icon={Clock}
            iconBg='bg-orange-100'
            iconColor='text-orange-600'
            label='En attente paiement'
            value={isLoading ? '—' : String(pending)}
            subtitle='Action requise'
          />
        </div>

        {/* Filters */}
        <div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-center'>
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
            <input
              type='text'
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder='Rechercher par nom d&#39;equipe...'
              className='w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-4 text-sm focus:border-orange-400 focus:outline-none'
            />
          </div>
          <select
            value={regStatusParam}
            onChange={e => setParam('regStatus', e.target.value)}
            className='rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-orange-400 focus:outline-none'
          >
            <option value=''>Statut inscription</option>
            {Object.entries(REG_STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          <select
            value={payStatusParam}
            onChange={e => setParam('payStatus', e.target.value)}
            className='rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-orange-400 focus:outline-none'
          >
            <option value=''>Statut paiement</option>
            {Object.entries(PAY_STATUS_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          {isFetching && !isLoading && <span className='text-xs text-slate-400'>Chargement...</span>}
        </div>

        {/* Table */}
        <div className='bg-white rounded-xl border border-slate-200'>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr className='border-b border-slate-100'>
                  <th
                    scope='col'
                    className='text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Equipe
                  </th>
                  <th
                    scope='col'
                    className='text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Concours
                  </th>
                  <th
                    scope='col'
                    className='text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Date
                  </th>
                  <th
                    scope='col'
                    className='text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Joueurs
                  </th>
                  <th
                    scope='col'
                    className='text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Inscription
                  </th>
                  <th
                    scope='col'
                    className='text-left py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Paiement
                  </th>
                  <th
                    scope='col'
                    className='text-right py-4 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider'
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <td key={j} className='py-4 px-6'>
                          <div className='h-4 animate-pulse rounded bg-slate-200 w-full' />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : teams.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='py-16 text-center'>
                      <div className='flex flex-col items-center gap-3'>
                        <ClipboardCheck className='h-12 w-12 text-slate-200' />
                        <p className='text-sm text-slate-500'>Aucune inscription trouvee</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  teams.map(team => {
                    const regCfg = REG_STATUS_CONFIG[team.registrationStatus];
                    const payCfg = PAY_STATUS_CONFIG[team.paymentStatus];
                    return (
                      <tr key={team.id} className='border-b border-slate-50 hover:bg-slate-50 transition'>
                        <td className='py-4 px-6'>
                          <p className='font-semibold text-slate-900 text-sm'>{team.name}</p>
                          {getTeamSportingCaptain(team) && (
                            <p className='text-xs text-slate-500'>
                              Cap. {getTeamSportingCaptain(team)?.firstName} {getTeamSportingCaptain(team)?.lastName}
                            </p>
                          )}
                        </td>
                        <td className='py-4 px-6'>
                          <p className='text-sm text-slate-700'>{team.competition?.name ?? `#${team.competitionId}`}</p>
                        </td>
                        <td className='py-4 px-6'>
                          <p className='text-sm text-slate-700'>
                            {team.competition?.date ? formatDateAbbreviatedFr(team.competition.date) : '—'}
                          </p>
                        </td>
                        <td className='py-4 px-6'>
                          <div className='flex items-center gap-2'>
                            {team.players && team.players.length > 0 ? (
                              <div className='flex -space-x-2'>
                                {team.players.slice(0, 4).map((p, idx) => (
                                  <div
                                    key={p.id}
                                    title={`${p.firstName} ${p.lastName}`}
                                    className={`w-8 h-8 bg-gradient-to-br ${GRADIENT_COLORS[idx % GRADIENT_COLORS.length]} rounded-full flex items-center justify-center font-bold text-white text-xs border-2 border-white`}
                                  >
                                    {getInitials(p.firstName, p.lastName)}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <Users className='h-4 w-4 text-slate-300' />
                            )}
                            <span className='text-xs text-slate-500'>{team.players?.length ?? 0}</span>
                          </div>
                        </td>
                        <td className='py-4 px-6'>
                          <Badge variant={regCfg.variant}>
                            {team.registrationStatus === TeamRegistrationStatus.CONFIRMED && (
                              <BadgeCheck className='w-3 h-3 mr-1 inline' />
                            )}
                            {regCfg.label}
                          </Badge>
                        </td>
                        <td className='py-4 px-6'>
                          <Badge variant={payCfg.variant}>
                            {team.paymentStatus === PaymentStatus.COMPLETED && (
                              <CreditCard className='w-3 h-3 mr-1 inline' />
                            )}
                            {payCfg.label}
                          </Badge>
                        </td>
                        <td className='py-4 px-6'>
                          <div className='flex items-center justify-end'>
                            <button
                              onClick={() => setSelectedTeam(team)}
                              aria-label={`Voir le detail de ${team.name}`}
                              className='p-1.5 rounded-lg hover:bg-slate-100 transition text-slate-400'
                              title='Voir'
                            >
                              <Eye className='w-4 h-4' />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
              <p className='text-sm text-slate-500'>
                <span className='font-semibold text-slate-700'>{from}</span> a{' '}
                <span className='font-semibold text-slate-700'>{to}</span> sur{' '}
                <span className='font-semibold text-slate-700'>{total}</span>
              </p>
              {totalPages > 1 && (
                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => setParam('page', String(Math.max(1, page - 1)))}
                    disabled={page === 1}
                    aria-label='Page precedente'
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </button>
                  {getPageNumbers(page, totalPages).map((p, i) =>
                    p === '...' ? (
                      <span key={`dots-${i}`} className='px-2 text-slate-400'>
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setParam('page', String(p))}
                        aria-current={p === page ? 'page' : undefined}
                        aria-label={`Page ${p}`}
                        className={`rounded-lg px-3 py-1 text-sm font-semibold transition ${
                          p === page ? 'bg-emerald-600 text-white' : 'border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() => setParam('page', String(Math.min(totalPages, page + 1)))}
                    disabled={page >= totalPages}
                    aria-label='Page suivante'
                    className='rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 disabled:opacity-40'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Detail Modal */}
        <TeamDetailDialog team={selectedTeam} onClose={() => setSelectedTeam(null)} />
      </div>
    </DashboardLayout>
  );
}
